import React, { useState, useEffect } from 'react';
import { ChevronRight, Palette, Globe } from 'lucide-react';
import { DomainModal } from './DomainModal';
import {
  Modal,
  Typography,
  Button,
  Input,
  Form,
  Switch,
  useToast,
} from '@crisellevaldez/speculo-ui';
import { useUpdateOrganizationBranding } from '../../../hooks/organizations/useUpdateOrganizationBranding';
import { Organization as BaseOrganization } from '../../../types/organization.types';

interface Organization extends BaseOrganization {
  brokerage_name?: string;
  combined_name?: string;
  logoUrl?: string;
  branding?: {
    primaryColor: string;
    secondaryColor: string;
  };
  org_links?: Record<string, string>;
  subdomain?: string;
}

interface SignFastModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: Organization;
  onSuccess?: () => void;
  setIsUnifiedBrandingModalOpen: (isOpen: boolean) => void;
}

const SignFastModal: React.FC<SignFastModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  onSuccess,
  setIsUnifiedBrandingModalOpen,
}) => {
  const [isDomainModalOpen, setIsDomainModalOpen] = useState(false);
  const [customName, setCustomName] = useState('');
  const [teamName, setTeamName] = useState('');
  const [brokerageName, setBrokerageName] = useState('');
  const [isCustomNameEnabled, setIsCustomNameEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { updateBranding } = useUpdateOrganizationBranding();
  const { showToast } = useToast();

  useEffect(() => {
    if (!selectedOrg || !isOpen) return;

    // Handle names
    const hasCombinedName = !!selectedOrg.combined_name;
    setIsCustomNameEnabled(hasCombinedName);
    setCustomName(selectedOrg.combined_name || '');
    setTeamName(selectedOrg.org_name || '');
    setBrokerageName(selectedOrg.brokerage_name || '');
  }, [selectedOrg, isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>
        <Typography variant="h3">SignFast</Typography>
      </Modal.Header>
      <Modal.Body>
        <div className="space-y-6">
          {/* SignFast Settings */}
          <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/10">
            {/* Domain and Branding */}
            <div className="mb-6">
              <Form.Label>Domain</Form.Label>
              <div className="flex gap-4">
                <div className="flex gap-4 flex-1">
                  <button
                    type="button"
                    onClick={() => setIsDomainModalOpen(true)}
                    className="flex-1 group flex items-center justify-between p-4 py-2 bg-gray-100 dark:bg-white/10 rounded-lg border border-gray-300 dark:border-white/15 hover:shadow-sm hover:scale-[1.02] transition-all duration-200"
                  >
                    <div className="flex flex-col gap-1 ">
                      <span className="text-sm text-black dark:text-gray-200">
                        {selectedOrg?.subdomain || '{subdomain}'}.signfast.ai
                      </span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-black dark:text-gray-300 group-hover:text-gray-600 dark:group-hover:text-gray-400 transition-colors" />
                  </button>
                  {selectedOrg?.subdomain && (
                    <a
                      href={`https://${selectedOrg.subdomain}.signfast.ai`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <Globe className="w-5 h-5 text-primary" />
                    </a>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={() => setIsUnifiedBrandingModalOpen(true)}
                  className="flex items-center gap-2"
                >
                  <Palette className="w-4 h-4" />
                  Update Branding
                </Button>
              </div>
            </div>

            {/* Name */}
            <div>
              <Form.Label>Name</Form.Label>
              {isCustomNameEnabled ? (
                <div className="flex flex-col md:flex-row gap-4">
                  <Form.Control className="w-full md:flex-1">
                    <Form.Label>Custom Name</Form.Label>
                    <Input value={customName} onChange={(e) => setCustomName(e.target.value)} />
                  </Form.Control>
                  <div className="flex items-center gap-2 mt-5">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Customize Name</span>
                    <Switch
                      size="sm"
                      checked={isCustomNameEnabled}
                      onChange={(e) => setIsCustomNameEnabled(e.target.checked)}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col md:flex-row gap-4">
                  <Form.Control className="w-full md:flex-1">
                    <Form.Label>Team Name</Form.Label>
                    <Input value={teamName} onChange={(e) => setTeamName(e.target.value)} />
                  </Form.Control>
                  <Form.Control className="w-full md:flex-1">
                    <Form.Label>Brokerage Name</Form.Label>
                    <Input
                      value={brokerageName}
                      onChange={(e) => setBrokerageName(e.target.value)}
                    />
                  </Form.Control>
                  <div className="flex items-center gap-2 mt-5">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Customize Name</span>
                    <Switch
                      size="sm"
                      checked={isCustomNameEnabled}
                      onChange={(e) => setIsCustomNameEnabled(e.target.checked)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>

      <DomainModal
        isOpen={isDomainModalOpen}
        onClose={() => setIsDomainModalOpen(false)}
        currentSubdomain={selectedOrg?.subdomain}
        onSuccess={async (subdomain) => {
          if (!selectedOrg) return;

          try {
            await updateBranding(selectedOrg.id, {
              subdomain,
              branding: {
                primaryColor: selectedOrg.branding?.primaryColor || 'rgb(0, 0, 0)',
                secondaryColor: selectedOrg.branding?.secondaryColor || 'rgb(207, 161, 82)',
              },
            });
            onSuccess?.();
          } catch (error) {
            console.error('Error updating subdomain:', error);
            showToast({
              message: error instanceof Error ? error.message : 'Failed to update subdomain',
              variant: 'error',
            });
          }
        }}
      />
      <Modal.Footer>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isLoading={isLoading}
          disabled={isLoading}
          onClick={async () => {
            if (!selectedOrg) return;

            try {
              setIsLoading(true);

              const updateData = isCustomNameEnabled
                ? { combined_name: customName }
                : {
                    org_name: teamName,
                    brokerage_name: brokerageName,
                    combined_name: '',
                  };

              await updateBranding(selectedOrg.id, updateData);

              showToast({
                message: 'Signfast settings updated successfully',
                variant: 'success',
              });
              onSuccess?.();
              onClose();
            } catch (error) {
              showToast({
                message:
                  error instanceof Error ? error.message : 'Failed to update branding settings',
                variant: 'error',
              });
            } finally {
              setIsLoading(false);
            }
          }}
        >
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export { SignFastModal as BrandingModal };
